import { ChangeEventHandler, FC, useState } from 'react';
import styles from './styles.module.css';
import { useFormContext } from 'react-hook-form';
import { ContractFormData } from '../../../../../types/contracts';
import { useGetUsersWithoutLoadingQuery } from '../../../../../api/userApi';
import FormItem from '../../../../../components/common/FormItem';
import Input from '../../../../../components/common/Input';
import { validateDateWithHyphen } from '../../../../../validators/date';
import { date, required, selected } from '../../../../../message';
import RadioButton from '../../../../../components/common/RadioButton';
import { useGetContractDocumentTypesQuery } from '../../../../../api/contractApi';
import { UserInList } from '../../../../../types/user';
import SuggestInput from '../../../../../components/common/SuggestInput';
import DateInput from '../../../../../components/common/DateInput';
import { format } from 'date-fns';
import { ErrorMessage } from '@hookform/error-message';
import Caption, {
  CaptionsUnderForm,
} from '../../../../../components/common/Caption';

const TypedFormItem = FormItem<ContractFormData>;

const labels: Pick<
  Record<keyof ContractFormData, string>,
  | 'contractNo'
  | 'receivedAt'
  | 'salesPic'
  | 'updatedBy'
  | 'contractDocumentTypeId'
> = {
  contractNo: '契約No',
  receivedAt: '受付日',
  salesPic: '営業担当',
  updatedBy: '最終更新者',
  contractDocumentTypeId: '区分',
};
const errorVariants = ['error' as const];

const setValueOpt = {
  shouldDirty: true,
  shouldTouch: true,
};
const General: FC = () => {
  const form = useFormContext<ContractFormData>();
  const salesPic = form.getValues('salesPic');
  const { register, formState, setValue, getValues, trigger, watch } = form;
  const [salesPicUserName, setSalesPicUserName] = useState(
    salesPic?.name ?? ''
  );
  const { data: { users } = { users: [] } } = useGetUsersWithoutLoadingQuery({
    userName: salesPicUserName,
  });
  const { data: documentTypes } = useGetContractDocumentTypesQuery();

  register('salesPic', {
    required: selected(labels.salesPic),
    validate: {
      inUser: (v) => v !== null || '存在するユーザーを指定してください',
    },
  });
  const onChangeSalesPicUserName = (userName: string) => {
    setSalesPicUserName(userName);
    const user = (users || []).find((u) => u.userName === userName);
    form.setValue(
      'salesPic',
      user ? { id: user.userId, name: user.userName } : null,
      setValueOpt
    );
  };
  const onClickSuggest = (user: UserInList) => {
    setSalesPicUserName(user.userName);
    setValue(
      'salesPic',
      user ? { id: user.userId, name: user.userName } : null,
      setValueOpt
    );
  };

  const onChangeContractDocumentTypeId: ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    const value = Number(e.target.value);
    setValue('contractDocumentTypeId', value);
  };

  const receivedAt = watch('receivedAt');
  register('receivedAt', {
    required: required(labels.receivedAt),
    validate: {
      date: (value) => validateDateWithHyphen(value) || date(labels.receivedAt),
    },
  });

  return (
    <>
      <div className={styles.line}>
        <div className={styles.contractId}>
          <TypedFormItem label={labels.contractNo}>
            <div className={styles.input}>
              <Input
                {...form.register('contractNo')}
                variants={['fit']}
                disabled
              />
            </div>
          </TypedFormItem>
        </div>
        <div className={styles.receivedAt}>
          <TypedFormItem label={labels.receivedAt} noLabelWrap={true}>
            <DateInput
              value={receivedAt ? new Date(receivedAt) : undefined}
              variants={formState.errors.receivedAt ? errorVariants : []}
              onChange={(day) => {
                setValue('receivedAt', day ? format(day, 'yyyy-MM-dd') : '', {
                  shouldValidate: true,
                });
              }}
              onBlur={() => trigger('receivedAt')}
            />
          </TypedFormItem>
        </div>
        <div className={styles.salesPic}>
          <TypedFormItem label={labels.salesPic}>
            <div className={styles.input}>
              <SuggestInput<UserInList>
                value={salesPicUserName}
                list={users || []}
                onClickSuggest={onClickSuggest}
                getKey={(item) => item.userId}
                getLabel={(item) => item.userName}
                onBlur={() => trigger('salesPic')}
                onChange={onChangeSalesPicUserName}
                variants={[
                  'fit',
                  ...(formState.errors.salesPic ? errorVariants : []),
                ]}
              />
            </div>
          </TypedFormItem>
        </div>
        <div className={styles.lastUpdatedBy}>
          <TypedFormItem label={labels.updatedBy}>
            <div className={styles.input}>
              <Input
                name="updatedBy"
                value={getValues('updatedBy')}
                variants={['fit']}
                disabled
              />
            </div>
          </TypedFormItem>
        </div>
        <div>
          <div className={styles.radioLabel}>
            {labels.contractDocumentTypeId}
          </div>
          <div className={styles.radioGroup}>
            {documentTypes?.map((t) => {
              return (
                <div key={t.contractDocumentTypeId} className={styles.radio}>
                  <RadioButton
                    name="contractDocumentTypeId"
                    value={t.contractDocumentTypeId}
                    label={t.contractDocumentType}
                    checked={
                      watch('contractDocumentTypeId') ===
                      Number(t.contractDocumentTypeId)
                    }
                    onChange={onChangeContractDocumentTypeId}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <CaptionsUnderForm>
        {Object.keys(labels).map((name) => (
          <ErrorMessage
            key={name}
            name={name}
            render={({ message }) => (
              <Caption variants={['error']}>{message}</Caption>
            )}
          />
        ))}
      </CaptionsUnderForm>
    </>
  );
};

export default General;
